import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';
import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Preset demonstrating domain + range features together.
 *
 * Shows two functions with restricted domains and ranges:
 * - sqrt(x): domain shading (vertical, sky) + range shading (horizontal, indigo)
 * - log(x): domain shading (vertical, green) + range shading (horizontal, orange)
 *
 * Domain bands are vertical (which x-values are valid).
 * Range bands are horizontal (which y-values the function outputs).
 */
export const TEST_DOMAIN_RANGE_PRESET: GraphpadWidgetConfig = {
  id: 'test-domain-range',
  type: 'graphpad',
  params: {
    f: {
      type: 'sqrt',
      params: { a: 1, h: 0, k: 0 },
    },
    'f-domain': {
      type: 'domain',
      params: { of: 'f' },
    },
    'f-range': {
      type: 'range',
      params: { of: 'f' },
    },
    g: {
      type: 'logarithmic',
      params: { a: 1, b: Math.E, h: 0, k: 0 },
    },
    'g-domain': {
      type: 'domain',
      params: { of: 'g' },
    },
    'g-range': {
      type: 'range',
      params: { of: 'g' },
    },
  },
  display: {
    canvas: { color: 'neutral-0' },
    coordinateSystem: {
      boundingbox: { xStart: -5, xEnd: 10, yStart: -4, yEnd: 5 },
      grid: { type: 'lines', visible: false },
    },
    elements: {
      f: { curve: { color: 'sky', strokeWidth: 3 } },
      'f-domain': {
        polygon: { fillColor: 'sky-50' },
        line: { color: 'sky-300', dash: 3 },
      },
      'f-range': {
        polygon: { fillColor: 'indigo-50' },
        line: { color: 'indigo-300', dash: 3 },
      },
      g: { curve: { color: 'green', strokeWidth: 3 } },
      'g-domain': {
        polygon: { fillColor: 'green-50' },
        line: { color: 'green-300', dash: 3 },
      },
      'g-range': {
        polygon: { fillColor: 'orange-50' },
        line: { color: 'orange-300', dash: 3 },
      },
    },
  },
  behavior: {
    controls: [
      {
        id: 'slider-h',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'h (sqrt x-shift)',
        param: 'f.h',
        range: [-5, 5, 0.5],
        defaultValue: 0,
        editable: true,
        color: 'sky',
      },
    ],
  },
};
